import { useNotifications } from '@lib/contexts/useNotifications'
import { useWeb3 } from '@lib/contexts/useWeb3'
import { IconBell } from '@lib/icons/IconBell'
import { IconBurgerPlain } from '@lib/icons/IconBurgerPlain'
import { IconCheckmark } from '@lib/icons/IconCheckmark'
import { IconWallet } from '@lib/icons/IconWallet'
import { cl } from '@lib/utils'
import {
  getIncidentAlertLink,
  getIncidentAlertLinkLabel,
  getIncidentAlertMessage,
  isIncidentActive
} from '@lib/utils/incident'
import {
  getReopeningAlertLink,
  getReopeningAlertLinkLabel,
  getReopeningAlertMessage,
  isReopeningActive
} from '@lib/utils/reopening'
import { truncateHex } from '@lib/utils/tools.address'
import { useAccountModal, useChainModal } from '@rainbow-me/rainbowkit'
import type { ReactElement } from 'react'
import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router'
import type { Chain } from 'viem'
import Image from '/src/components/Image'
import Link from '/src/components/Link'
import { APPS, AppName } from './Apps'
import { Button } from './Button'
import { ModalMobileMenu } from './ModalMobileMenu'

export type TMenu = {
  path: string
  label: string | ReactElement
  target?: string
}
type TNavbar = { nav: TMenu[]; currentPathName: string }

function Navbar({ nav, currentPathName }: TNavbar): ReactElement {
  return (
    <nav className={'yearn--nav'}>
      {nav.map(
        (option): ReactElement => (
          <Link key={option.path} target={option.target} href={option.path}>
            <p className={`yearn--header-nav-item ${currentPathName.startsWith(option.path) ? 'active' : ''}`}>
              {option?.label || 'Unknown'}
            </p>
          </Link>
        )
      )}
    </nav>
  )
}

function IncidentAlert(): ReactElement | null {
  if (!isIncidentActive()) {
    return null
  }

  const message = getIncidentAlertMessage()
  const link = getIncidentAlertLink()

  return (
    <div role={'alert'} className={'w-full bg-red p-2 text-center text-xs text-white'} data-testid={'incident-alert'}>
      {message}
      {link && (
        <>
          {' '}
          <Link href={link} target={'_blank'} className={'underline'}>
            {getIncidentAlertLinkLabel()}
          </Link>
        </>
      )}
    </div>
  )
}

function ReopeningAlert(): ReactElement | null {
  if (!isReopeningActive()) {
    return null
  }

  const message = getReopeningAlertMessage()
  const link = getReopeningAlertLink()

  return (
    <div
      role={'alert'}
      className={'flex w-full items-center justify-center gap-1 bg-[#00796D] p-2 text-center text-xs text-white'}
      data-testid={'reopening-alert'}
    >
      <IconCheckmark className={'size-3 shrink-0'} />
      <span>
        {message}
        {link && (
          <>
            {' '}
            <Link href={link} target={'_blank'} className={'underline'}>
              {getReopeningAlertLinkLabel()}
            </Link>
          </>
        )}
      </span>
    </div>
  )
}

function WalletSelector(): ReactElement {
  const { openAccountModal } = useAccountModal()
  const { openChainModal } = useChainModal()
  const { isActive, address, ens, clusters, lensProtocolHandle, openLoginModal } = useWeb3()
  const [walletIdentity, setWalletIdentity] = useState<string | undefined>(undefined)

  useEffect((): void => {
    if (!isActive && address) {
      setWalletIdentity('Invalid Network')
    } else if (ens) {
      setWalletIdentity(ens)
    } else if (clusters) {
      setWalletIdentity(clusters.name)
    } else if (lensProtocolHandle) {
      setWalletIdentity(lensProtocolHandle)
    } else if (address) {
      setWalletIdentity(truncateHex(address, 4))
    } else {
      setWalletIdentity(undefined)
    }
  }, [ens, clusters, lensProtocolHandle, address, isActive])

  return (
    <div
      onClick={(): void => {
        if (isActive) {
          openAccountModal?.()
        } else if (!isActive && address) {
          openChainModal?.()
        } else {
          openLoginModal()
        }
      }}
    >
      <p suppressHydrationWarning className={'yearn--header-nav-item text-xs! md:text-sm!'}>
        {walletIdentity ? (
          walletIdentity
        ) : (
          <span>
            <IconWallet className={'yearn--header-nav-item mt-0.5 block size-4 md:hidden'} />
            <span
              className={
                'text-neutral-0 relative hidden h-8 cursor-pointer items-center justify-center rounded-sm border border-transparent bg-neutral-900 px-2 text-xs font-normal transition-all hover:bg-neutral-800 md:flex'
              }
            >
              {'Connect wallet'}
            </span>
          </span>
        )}
      </p>
    </div>
  )
}

function AppHeader(props: { supportedNetworks: Chain[] }): ReactElement {
  const location = useLocation()
  const navigate = useNavigate()
  const pathname = location.pathname
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false)
  const { setShouldOpenCurtain, notificationStatus } = useNotifications()

  const menu = useMemo((): TMenu[] => {
    if (pathname.startsWith('/vaults-beta')) {
      return [...APPS[AppName.BETA].menu]
    }

    return [...APPS[AppName.VAULTSV3].menu]
  }, [pathname])

  const notificationDotColor = useMemo(() => {
    if (notificationStatus === 'error') {
      return 'bg-red'
    }

    if (notificationStatus === 'success') {
      return 'bg-[#0C9000]'
    }
    if (notificationStatus === 'pending') {
      return 'bg-primary animate-pulse'
    }

    return ''
  }, [notificationStatus])

  return (
    <div id={'head'} className={'inset-x-0 top-0 z-50 w-full'}>
      <IncidentAlert />
      <ReopeningAlert />
      <div className={'w-full'}>
        <header className={'yearn--header mx-auto max-w-[1232px] px-0!'}>
          <div className={'direction-row flex items-center justify-start gap-x-6 px-1 py-2 md:py-1'}>
            <div className={'flex justify-center cursor-pointer'} onClick={() => navigate('/')}>
              <Image width={32} height={32} alt={'chain'} src={'/sf-logo.svg'} />
            </div>
            <Navbar currentPathName={pathname || ''} nav={menu} />
          </div>
          <div className={'flex w-1/3 items-center justify-end'}>
            <div className="yearn--nav">
              <Button
                variant="inherit"
                as="a"
                href={import.meta.env.VITE_FIXED_INCOME_MARKET_LINK || 'https://app.secured.finance/'}
                target="_blank"
                className="text-neutral-500 hover:text-neutral-700 px-3!"
              >
                Fixed Income
              </Button>
            </div>
            <button
              className={'yearn--header-nav-item relative rounded-full p-2 transition-colors'}
              onClick={(): void => setShouldOpenCurtain(true)}
            >
              <IconBell className={'size-4 font-bold transition-colors'} />

              <div className={cl('absolute right-1 top-1 size-2 rounded-full', notificationDotColor)} />
            </button>
            <WalletSelector />
            <div className={'flex md:hidden pl-4 text-neutral-500'}>
              <button onClick={(): void => setIsMenuOpen(!isMenuOpen)}>
                <span className={'sr-only'}>{'Open menu'}</span>
                <IconBurgerPlain />
              </button>
            </div>
          </div>
        </header>
      </div>
      <ModalMobileMenu
        shouldUseWallets={true}
        shouldUseNetworks={true}
        isOpen={isMenuOpen}
        onClose={(): void => setIsMenuOpen(false)}
        supportedNetworks={props.supportedNetworks}
      >
        {menu?.map(
          (option): ReactElement => (
            <Link key={option.path} href={option.path}>
              <div className={'mobile-nav-item'} onClick={(): void => setIsMenuOpen(false)}>
                <p className={'font-bold'}>{option.label}</p>
              </div>
            </Link>
          )
        )}
      </ModalMobileMenu>
    </div>
  )
}

export default AppHeader
